/**
 * Voice AI — Settings Service
 * Owns all settings config API calls. Sub-components call this service
 * instead of making direct fetch calls.
 */

import { authService } from './auth-service.js';

const API_BASE = '/voice/php/api';

class SettingsService {

  /* ─── General Config ─── */

  async loadGeneralConfig() {
    const res = await authService.apiFetch(`${API_BASE}/general-config.php`);
    return res.json();
  }

  async saveGeneralConfig(data) {
    const res = await authService.apiFetch(`${API_BASE}/general-config.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    return res.json();
  }

  /* ─── Twilio Config ─── */

  async loadTwilioConfig() {
    const res = await authService.apiFetch(`${API_BASE}/twilio-config.php`);
    return res.json();
  }

  async saveTwilioConfig(data) {
    const res = await authService.apiFetch(`${API_BASE}/twilio-config.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    return res.json();
  }

  async testTwilioConnection(sid, token) {
    const res = await authService.apiFetch(`${API_BASE}/twilio-test.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ account_sid: sid, auth_token: token })
    });
    return res.json();
  }

  /* ─── Security Config ─── */

  async loadSecurityConfig() {
    const res = await authService.apiFetch(`${API_BASE}/security-config.php`);
    return res.json();
  }

  async saveSecurityConfig(data) {
    const res = await authService.apiFetch(`${API_BASE}/security-config.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    return res.json();
  }

  /* ─── Webhooks Config ─── */

  async loadWebhooksConfig() {
    const res = await authService.apiFetch(`${API_BASE}/webhooks-config.php`);
    return res.json();
  }

  async saveWebhooksConfig(data) {
    const res = await authService.apiFetch(`${API_BASE}/webhooks-config.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    return res.json();
  }

  /* ─── Pipeline Config ─── */

  async loadPipelineConfig() {
    const res = await authService.apiFetch(`${API_BASE}/pipeline-config.php`);
    return res.json();
  }

  async savePipelineConfig(data) {
    const res = await authService.apiFetch(`${API_BASE}/pipeline-config.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    return res.json();
  }

  /* ─── Payment Config ─── */

  async loadPaymentConfig() {
    const res = await authService.apiFetch(`${API_BASE}/payment-settings.php`);
    return res.json();
  }

  async savePaymentConfig(data) {
    const res = await authService.apiFetch(`${API_BASE}/payment-settings.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    return res.json();
  }

  async loadPaymentCalculation(orderId) {
    const url = orderId
      ? `${API_BASE}/payment-config.php?order_id=${orderId}`
      : `${API_BASE}/payment-config.php`;
    const res = await authService.apiFetch(url);
    return res.json();
  }

  async setTestMode(enabled) {
    const res = await authService.apiFetch(`${API_BASE}/payment-test-mode.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ enabled })
    });
    return res.json();
  }

  /* ─── Monitor Config ─── */

  async loadMonitorConfig() {
    const res = await authService.apiFetch(`${API_BASE}/monitor-config.php`);
    return res.json();
  }

  async saveMonitorConfig(data) {
    const res = await authService.apiFetch(`${API_BASE}/monitor-config.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    return res.json();
  }

  /* ─── Monitor Actions & Proposals ─── */

  async loadMonitorActions(page = 1, limit = 20) {
    const res = await authService.apiFetch(`${API_BASE}/monitor-actions.php?page=${page}&limit=${limit}`);
    return res.json();
  }

  async loadMonitorAction(id) {
    const res = await authService.apiFetch(`${API_BASE}/monitor-actions.php?id=${id}`);
    return res.json();
  }

  async loadMonitorProposals(page = 1, limit = 20, status = '') {
    const url = status
      ? `${API_BASE}/monitor-actions.php?type=proposals&page=${page}&limit=${limit}&status=${status}`
      : `${API_BASE}/monitor-actions.php?type=proposals&page=${page}&limit=${limit}`;
    const res = await authService.apiFetch(url);
    return res.json();
  }

  async loadMonitorProposal(id) {
    const res = await authService.apiFetch(`${API_BASE}/monitor-actions.php?type=proposals&id=${id}`);
    return res.json();
  }

  async updateProposal(id, action, data = {}) {
    const res = await authService.apiFetch(`${API_BASE}/monitor-actions.php?type=proposals&id=${id}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action, ...data })
    });
    return res.json();
  }

  /* ─── Monitor Chat ─── */

  async loadMonitorChat(limit = 50) {
    const res = await authService.apiFetch(`${API_BASE}/monitor-chat.php?limit=${limit}`);
    return res.json();
  }

  async stopMonitorSession() {
    const res = await authService.apiFetch(`${API_BASE}/monitor-config.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'stop_session' })
    });
    return res.json();
  }

  async sendMonitorChat(message) {
    const res = await authService.apiFetch(`${API_BASE}/monitor-chat.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ message })
    });
    return res.json();
  }

  /* ─── Vision Config ─── */

  async loadVisionConfig() {
    const res = await authService.apiFetch(`${API_BASE}/vision-config.php`);
    return res.json();
  }

  async saveVisionConfig(data) {
    const res = await authService.apiFetch(`${API_BASE}/vision-config.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    return res.json();
  }

  async testVisionConnection() {
    // Simple test: try to analyze a tiny 1x1 white pixel
    const testPixel = 'iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mP8/5+hHgAHggJ/PchI7wAAAABJRU5ErkJggg==';
    const res = await authService.apiFetch(`${API_BASE}/vision-analyze.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ image_base64: testPixel, media_type: 'image/png', features: ['LABEL_DETECTION'] })
    });
    return res.json();
  }

  /* ─── Phone Routing (read-only) ─── */

  async loadPhoneRouting() {
    const res = await authService.apiFetch(`${API_BASE}/phone-routing.php`);
    const data = await res.json();
    return { success: data.success, routes: data.routes || [] };
  }
}

export const settingsService = new SettingsService();
